import { useEffect, useMemo, useState, useRef } from "react";
import { Modal, Form, Badge } from "react-bootstrap";
import { Button } from "./Controls/Button";
import { Trash } from "react-bootstrap-icons";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { nanoid } from "nanoid";

export enum RuleType {
  Discount = 0,
  Fee = 1,
}

export type RuleUI = ContractRuleForm & {
  id: string;
};

export type ContractRuleForm = {
  min?: number;
  max?: number;
  noCost: boolean;
  type: RuleType;
  price?: number;

  _priceText?: string;
  _isPriceFocused?: boolean;
};

export type ContractRuleContext =
  | { mode: "plan" }
  | { mode: "group"; groupId: number | null };

export interface ContractRuleModalProps {
  show: boolean;
  onClose: () => void;
  context: ContractRuleContext;
  groups?: { id: number; name: string }[];
  onSubmit: (data: {
    groupId: number;
    rules: ContractRuleForm[];
  }) => Promise<void>;
  isEditing: boolean;

  initialRules?: {
    min?: number;
    max?: number;
    price?: number;
  }[];

  onRemoveContract: (groupId: number) => Promise<void>;
}

export function ContractRuleModal({
  show,
  onClose,
  context,
  groups = [],
  onSubmit,
  isEditing,
  initialRules,
  onRemoveContract,
}: ContractRuleModalProps) {
  const [groupId, setGroupId] = useState<number | null>(null);
  const [rules, setRules] = useState<ContractRuleForm[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [newlyAddedRuleIndex, setNewlyAddedRuleIndex] = useState<number | null>(
    null,
  );

  const isPlanMode = context.mode === "plan";

  const selectedGroupName = useMemo(() => {
    if (context.mode === "group" && context.groupId) {
      return groups.find((g) => g.id === context.groupId)?.name ?? null;
    }
    return null;
  }, [context, groups]);

  useEffect(() => {
    if (context.mode === "group") {
      setGroupId(context.groupId);
    } else {
      setGroupId(null);
    }

    if (isEditing && initialRules?.length! > 0) {
      setRules(
        initialRules!.map((r) => ({
          min: r.min ?? undefined,
          max: r.max ?? undefined,
          noCost: r.price == null || r.price === 0,
          price: r.price != null ? Math.abs(r.price) : undefined,
          type: r.price! > 0 ? RuleType.Fee : RuleType.Discount,
        })),
      );
    } else {
      setRules([]);
    }
    setError(null);
  }, [show, context]);

  const rangesOverlap = (a: ContractRuleForm, b: ContractRuleForm) => {
    const minA = a.min ?? -Infinity;
    const maxA = a.max ?? Infinity;
    const minB = b.min ?? -Infinity;
    const maxB = b.max ?? Infinity;

    return minA <= maxB && minB <= maxA;
  };

  const validateRules = () => {
    if (rules.length === 0) {
      return "At least one rule must be added.";
    }
    for (let i = 0; i < rules.length; i++) {
      const r = rules[i];

      if (r.min == null && r.max == null) {
        return "Each rule must have a minimum or maximum volume.";
      }

      if (r.min != null && r.max != null && r.max < r.min) {
        return "Maximum volume cannot be less than minimum volume.";
      }

      if (!r.noCost && (r.price == null || r.price <= 0)) {
        return "price is required unless No Cost is selected.";
      }

      for (let j = i + 1; j < rules.length; j++) {
        if (rangesOverlap(r, rules[j])) {
          return "Rule volume ranges cannot overlap.";
        }
      }
    }

    return null;
  };

  function createEmptyRule() {
    return {
      min: undefined,
      max: undefined,
      noCost: false,
      type: RuleType.Discount,
      price: undefined,
      id: nanoid(),

      // UI-only fields
      _priceText: undefined,
      _isPriceFocused: false,
    };
  }

  function addRule() {
    const newRule = createEmptyRule();
    setRules((prev) => {
      const updated = [...prev, newRule].sort((a, b) => {
        const aMin = a.min ?? Number.MAX_SAFE_INTEGER;
        const bMin = b.min ?? Number.MAX_SAFE_INTEGER;
        return aMin - bMin;
      });
      // Find the index of the newly added rule after sorting
      const newIndex = updated.findIndex(
        (r) => (r as any).id === (newRule as any).id,
      );
      if (newIndex !== -1) {
        setNewlyAddedRuleIndex(newIndex);
      }
      return updated;
    });
  }

  const updateRule = (index: number, patch: Partial<ContractRuleForm>) => {
    setRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    );
  };

  const removeRule = (index: number) =>
    setRules((r) => r.filter((_, i) => i !== index));

  const submit = async () => {
    setError(null);

    const validationError = validateRules();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!groupId) {
      setError("Group is required.");
      return;
    }

    try {
      setSaving(true);
      await onSubmit({
        groupId,
        rules,
      });
      reorderRules();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const sortedRules = useMemo(() => {
    return [...rules].sort((a, b) => {
      const aMin = a.min ?? Number.MAX_SAFE_INTEGER;
      const bMin = b.min ?? Number.MAX_SAFE_INTEGER;
      return aMin - bMin;
    });
  }, [rules]);

  const reorderRules = () => {
    setRules((r) => [...r].sort((a, b) => (a.min ?? 0) - (b.min ?? 0)));
  };

  const gaps = useMemo(() => {
    return sortedRules
      .map((r, i) => {
        if (i === 0) return null;

        const prevMax = sortedRules[i - 1].max ?? 0;
        const curMin = r.min ?? 0;

        if (curMin > prevMax + 1) {
          return {
            from: prevMax + 1,
            to: curMin - 1,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [sortedRules]);

  const formatCurrency = (cents?: number) =>
    cents != null
      ? (cents / 100).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })
      : "";

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditing ? "Edit Contract" : "Add Contract"}
          {selectedGroupName && (
            <div className="text-muted fs-6">{selectedGroupName}</div>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {rules.length > 0 && (
          <div className="mb-3">
            <h6 className="mb-1">Tier Coverage</h6>
            <div
              className="d-flex align-items-center gap-1"
              style={{ height: 24 }}
            >
              {sortedRules.flatMap((rule, i) => {
                const prevMax = i > 0 ? (rules[i - 1].max ?? 0) : 0;
                const curMin = rule.min ?? 0;

                const elements: JSX.Element[] = [];

                if (i > 0 && curMin > prevMax + 1) {
                  elements.push(
                    <div
                      key={`gap-${i}`}
                      className="bg-warning"
                      style={{
                        flex: curMin - prevMax - 1,
                        borderRadius: 4,
                        opacity: 0.3,
                      }}
                    />,
                  );
                }

                const width =
                  rule.min != null && rule.max != null
                    ? rule.max - rule.min + 1
                    : 1;

                elements.push(
                  <div
                    key={`rule-bar-${i}`}
                    className="bg-primary text-white text-center"
                    style={{
                      flex: width,
                      borderRadius: 4,
                      fontSize: 10,
                    }}
                  >
                    {rule.min}-{rule.max}
                  </div>,
                );

                return elements;
              })}
            </div>
          </div>
        )}

        {isPlanMode && (
          <Form.Group className="mb-3">
            <Form.Label>Group</Form.Label>
            <Form.Select
              value={groupId ?? ""}
              onChange={(e) => setGroupId(Number(e.target.value))}
            >
              <option value="" disabled>
                Select group
              </option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        <div className="mb-2">
          <h6 className="mb-1">Criteria</h6>
          <div className="text-muted small">
            Set pricing tiers based on number of parkers in the group
          </div>
        </div>

        {rules.length === 0 && (
          <div className="d-flex flex-column align-items-center justify-content-center text-center py-4 border rounded mb-3">
            <div
              className="rounded-circle mb-3"
              style={{
                width: 72,
                height: 72,
                backgroundColor: "rgba(220, 53, 69, 0.15)",
              }}
            />
            <p className="text-secondary mb-4">No tiers added yet</p>
            <Button size="sm" variant="outline-primary" onClick={addRule}>
              + Add Tier
            </Button>
          </div>
        )}

        {rules.map((rule, i) => (
          <div key={i} className="border rounded p-3 mb-3">
            <div className="d-flex justify-content-between mb-3 align-items-center">
              <Badge bg="secondary">Tier {i + 1}</Badge>

              <button
                className="btn btn-sm"
                title="Delete Rule"
                onClick={() => removeRule(i)}
              >
                <Trash />
              </button>
            </div>

            <div className="row g-3 align-items-end mb-2">
              <div className="col-md-6">
                <div className="text-muted small mb-1">Parker Count Range</div>
                <div className="row g-2">
                  <div className="col">
                    <Form.Label className="small">From</Form.Label>
                    <Form.Control
                      ref={(el) => {
                        if (i === newlyAddedRuleIndex && el) {
                          setTimeout(() => el.focus(), 0);
                          setNewlyAddedRuleIndex(null);
                        }
                      }}
                      type="number"
                      value={rule.min ?? ""}
                      onChange={(e) =>
                        updateRule(i, {
                          min: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        })
                      }
                    />
                  </div>
                  <div className="col">
                    <Form.Label className="small">To</Form.Label>
                    <Form.Control
                      type="number"
                      value={rule.max ?? ""}
                      onChange={(e) =>
                        updateRule(i, {
                          max: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <Form.Label className="small">Type</Form.Label>
                <div className="d-flex gap-3 mt-2">
                  <Form.Check
                    type="radio"
                    label="Discount"
                    name={`type-${i}`}
                    checked={rule.type === RuleType.Discount}
                    disabled={rule.noCost}
                    onChange={() => updateRule(i, { type: RuleType.Discount })}
                  />
                  <Form.Check
                    type="radio"
                    label="Fee"
                    name={`type-${i}`}
                    checked={rule.type === RuleType.Fee}
                    disabled={rule.noCost}
                    onChange={() => updateRule(i, { type: RuleType.Fee })}
                  />
                </div>
              </div>

              <div className="col-md-3">
                <Form.Label className="small">Price</Form.Label>
                <Form.Control
                  type="text"
                  disabled={rule.noCost}
                  value={
                    rule._isPriceFocused
                      ? (rule._priceText ?? "")
                      : rule.price != null
                        ? (rule.price / 100).toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })
                        : ""
                  }
                  onFocus={() =>
                    updateRule(i, {
                      _isPriceFocused: true,
                      _priceText:
                        rule.price != null ? (rule.price / 100).toString() : "",
                    })
                  }
                  onChange={(e) =>
                    updateRule(i, {
                      _priceText: e.target.value.replace(/[^0-9.]/g, ""),
                    })
                  }
                  onBlur={() => {
                    const value = rule._priceText
                      ? Math.round(parseFloat(rule._priceText) * 100)
                      : undefined;

                    updateRule(i, {
                      _isPriceFocused: false,
                      price: isNaN(value as number) ? undefined : value,
                      _priceText: undefined,
                    });
                  }}
                />
              </div>
            </div>

            <Form.Check
              className="mt-2"
              label="No Cost"
              checked={rule.noCost}
              onChange={(e) =>
                updateRule(i, {
                  noCost: e.target.checked,
                  price: undefined,
                  _priceText: undefined,
                  _isPriceFocused: false,
                  type: e.target.checked ? RuleType.Discount : rule.type,
                })
              }
            />
            {rule.min != null &&
              rule.max != null &&
              (rule.price != null || rule.noCost) && (
                <div className="text-muted small mt-3">
                  This group with {rule.min} to {rule.max} parkers{" "}
                  {rule.noCost ? (
                    <>
                      will park{" "}
                      <strong>for free</strong>
                    </>
                  ) : rule.type === RuleType.Fee ? (
                    <>
                      pay an additional{" "}
                      <strong>{formatCurrency(Math.abs(rule.price!))}</strong> /
                      month
                    </>
                  ) : (
                    <>
                      receive a discount of{" "}
                      <strong>{formatCurrency(rule.price)}</strong> / month
                    </>
                  )}
                </div>
              )}
          </div>
        ))}

        {gaps.map((gap, i) => (
          <div key={i} className="alert alert-warning small">
            Gap detected: {gap!.from}-{gap!.to} parkers are not covered
          </div>
        ))}

        {rules.length > 0 && (
          <Button size="sm" variant="outline-primary" onClick={addRule}>
            + Add Tier
          </Button>
        )}

        {error && <div className="alert alert-danger mt-3">{error}</div>}
      </Modal.Body>

      <Modal.Footer className="d-flex align-items-center">
        <div className="me-auto">
          {isEditing && (
            <Button
              variant="outline-danger"
              disabled={saving || !groupId}
              onClick={() => setShowRemoveConfirm(true)}
            >
              <Trash className="icon-shift-up me-1" />
              Remove Contract
            </Button>
          )}
        </div>

        <div className={`d-flex gap-2 ${isEditing ? "mt-4 mt-md-0" : ""}`}>
          <Button variant="outline-secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button className="btn-main" onClick={submit} disabled={saving}>
            Save Contract
          </Button>
        </div>
      </Modal.Footer>

      <ConfirmModal
        title="Remove Contract"
        message="Are you sure you want to remove this contract?"
        show={showRemoveConfirm}
        setShow={setShowRemoveConfirm}
        onConfirm={async () => {
          if (!groupId) return;
          try {
            setSaving(true);
            await onRemoveContract(groupId);
            setShowRemoveConfirm(false);
            onClose();
          } finally {
            setSaving(false);
          }
        }}
      />
    </Modal>
  );
}
